import { brand } from '../../theme/themePrimitives';
import CardWrapper from '../wrapper/card';
import CustomDropdown from '../reusable/dropdown';
import DueTasksComponent from './due_task';
import { fetchCustomerAnalyticsDataThunk } from '../../redux/thunks/chart';
import { processCustomerGraphData } from './process_graph_data';
import ReactApexChart from 'react-apexcharts';
import RecentFilesComponent from './recent_files';
import { AppDispatch, RootState } from '../../redux/store';
import { AttachMoneyOutlined, InsertChartOutlined, SupervisedUserCircleOutlined } from '@mui/icons-material';
import { Box, SelectChangeEvent, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';

type TimeType = 'All time' | 'Last 6 months' | 'Last 3 months' | 'Last month' | 'Last week';

const CustomerDashboard = () => {
    const dispatch = useDispatch<AppDispatch>();
    const chartRef = useRef<HTMLDivElement>(null);
    const [selectedTime, setSelectedTime] = useState<TimeType>('Last week');
    const { currentUser } = useSelector((state: RootState) => state.app);
    const { loaders, customer_dashboard } = useSelector((state: RootState) => state.chart);
    const { months, totalLeads, totalLeadConversion } = processCustomerGraphData(customer_dashboard.customerAnalytics);
    const dateData = [
        { label: 'Last week', value: 'Last week' },
        { label: 'Last Month', value: 'Last month' },
        { label: 'Last 3 Months', value: 'Last 3 months' },
        { label: 'Last 6 Months', value: 'Last 6 months' },
        { label: 'All Time', value: 'All time' }
    ];
    const chartOptions: ApexCharts.ApexOptions = {
        chart: {
            type: 'line',
            height: 400,
            zoom: {
                enabled: false
            }
        },
        xaxis: {
            categories: months
        },
        stroke: {
            curve: 'smooth'
        }
    };
    const chartSeries: ApexAxisChartSeries = [
        { name: 'Revenue', data: totalLeads },
        { name: 'Converted Leads', data: totalLeadConversion }
    ];

    const statisticsDetail = [
        {
            label: 'Total Leads',
            total: customer_dashboard.total_leads,
            percentage: '0.0%',
            icon: <SupervisedUserCircleOutlined color="success" />,
            color: 'green'
        },
        {
            label: 'Revenue Generated',
            total: '$ 0',
            percentage: '0.0%',
            icon: <AttachMoneyOutlined color="primary" />,
            color: 'blue'
        },
        {
            label: 'Lead Converstion Rate',
            total: `${customer_dashboard.total_conversions.toFixed(2)}%`,
            percentage: '0.00%',
            icon: <InsertChartOutlined color="error" />,
            color: 'red'
        },
        {
            label: '',
            total: '',
            percentage: '',
            icon: <></>,
            color: 'white'
        }
    ];

    const timeChangeHandler = async (event: SelectChangeEvent<unknown>) => {
        event.preventDefault();
        const target = event.target.value;
        try {
            await dispatch(fetchCustomerAnalyticsDataThunk(target as TimeType)).unwrap();
            setSelectedTime(target as TimeType);
        } catch (e) {
            //
        }
    };

    useEffect(() => {
        const fetchAnalyticsData = async () => {
            try {
                await dispatch(fetchCustomerAnalyticsDataThunk(selectedTime)).unwrap();
            } catch (e) {
                //
            }
        };
        fetchAnalyticsData();
    }, []);

    return <CardWrapper minHeight="80vh" overflow="auto">
        <Typography variant="h6" mb={2} fontWeight="bold">
            Welcome <Typography component="span" variant="h6" sx={{ color: brand[300] }}>{currentUser?.name || `${currentUser?.first_name} ${currentUser?.last_name}`}</Typography>
        </Typography>
        <CardWrapper ref={chartRef}>
            <Box display="flex" justifyContent="space-between" alignItems="center"><Typography variant="h6" mb={2} fontWeight="bold">
                Statistics
            </Typography>
                <Box>
                    <CustomDropdown
                        options={dateData}
                        onChange={timeChangeHandler}
                        error={false}
                        value={selectedTime}
                        disabled={loaders.isLoading}
                    />
                </Box>
            </Box>
            <Box display="flex" alignItems="start" justifyContent="space-between">
                {
                    statisticsDetail.map((stat, index) => {
                        return <Box display="flex" gap="10px" key={index}>
                            <Box height="40px" width="40px" display="flex" borderRadius="10px" alignItems="center" justifyContent="center" sx={{
                                backgroundColor: stat.color === 'blue' ? 'rgba(0, 0, 255, 4%)' : stat.color === 'red' ? 'rgba(255, 0, 0, 4%)' : stat.color === 'green' ? 'rgba(0, 255, 0, 4%)' : 'white'
                            }}>{stat.icon}</Box>
                            <Box>
                                <Typography>{stat.label}</Typography>
                                <Typography><b>{stat.total}</b></Typography>
                            </Box>
                        </Box>;
                    })
                }
            </Box>
            <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={400} />
        </CardWrapper>
        <Box display="flex" gap="10px" my={2}>
            <DueTasksComponent />
            <RecentFilesComponent />
        </Box>
    </CardWrapper>;
};

export default CustomerDashboard;